import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Overline, Spinner, Empty, Pill } from "@/components/Widgets";
import { Image as ImageIcon, Sparkle, Trash, DownloadSimple, UploadSimple, MagicWand, X } from "@phosphor-icons/react";
import { PageGuide } from "@/components/PageGuide";

const STYLES = ["modern editorial", "cinematic", "vibrant lifestyle", "minimal luxury", "bold streetwear", "dreamy pastel", "high-fashion studio", "golden hour"];
const ASPECTS = [
  { id: "square", label: "Square", sub: "1:1", box: "w-5 h-5" },
  { id: "portrait", label: "Portrait", sub: "9:16", box: "w-3.5 h-5" },
  { id: "landscape", label: "Landscape", sub: "16:9", box: "w-6 h-3.5" },
];
const ENHANCE_PRESETS = [
  { label: "Red Carpet", prompt: "Transform into a glamorous Hollywood red-carpet portrait: dramatic spotlight, paparazzi flash bokeh, luxury styling, cinematic contrast" },
  { label: "Magazine Cover", prompt: "Editorial magazine-cover portrait, high-fashion studio lighting, glossy retouch, bold cinematic color grade" },
  { label: "Studio Headshot", prompt: "Premium studio headshot with soft key lighting, clean seamless backdrop, crisp professional retouch" },
  { label: "Film Noir", prompt: "Classic Hollywood film-noir portrait, dramatic black-and-white chiaroscuro lighting, moody cinematic shadows" },
  { label: "Golden Hour", prompt: "Cinematic golden-hour lifestyle portrait, warm sun-kissed glow, shallow depth of field, aspirational vibe" },
  { label: "Neon Nightlife", prompt: "Urban neon nightlife portrait, electric magenta and cyan glow, city lights bokeh, hip-hop luxury energy" },
  { label: "Executive", prompt: "Polished executive brand portrait, tailored, confident, upscale corporate studio lighting" },
  { label: "Album Cover", prompt: "Bold music album-cover style portrait, high-contrast dramatic lighting, artistic grade, star power" },
  { label: "Vintage Film", prompt: "Retro vintage film portrait, 70s Kodachrome color grade, subtle film grain, nostalgic warm tones, analog aesthetic" },
  { label: "Editorial B&W", prompt: "High-end editorial black-and-white portrait, rich tonal range, fine-art studio lighting, timeless elegance" },
  { label: "Luxury Spread", prompt: "Luxury magazine-spread portrait, opulent set design, designer styling, rich golden accents, ultra-premium fashion editorial" },
  { label: "Street Style", prompt: "Candid urban street-style portrait, gritty city backdrop, natural light, effortless cool, streetwear fashion energy" },
  { label: "Cover Story", prompt: "Time-magazine cover-story portrait, authoritative and iconic, dramatic single-source lighting, powerful and influential presence" },
  { label: "Glam Beauty", prompt: "Glossy beauty campaign close-up, flawless skin retouch, radiant glam makeup lighting, soft luminous glow" },
  { label: "Cinematic Teal", prompt: "Blockbuster cinematic portrait with teal-and-orange color grade, anamorphic lens flare, film-still drama" },
  { label: "Corporate CEO", prompt: "Fortune-500 CEO portrait, sleek modern office backdrop, confident power pose, premium corporate lighting" },
];

export default function ImageStudio() {
  const [mode, setMode] = useState("generate"); // generate | enhance
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("modern editorial");
  const [aspect, setAspect] = useState("square");
  const [refs, setRefs] = useState([]); // data URLs
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]);
  const [hasFace, setHasFace] = useState(false);
  const [useFace, setUseFace] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/images").then(({ data }) => setImages(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/profile").then(({ data }) => setHasFace(!!data.has_face)).catch(() => {});
  }, []);

  const onFiles = (files) => {
    const list = Array.from(files).slice(0, 2 - refs.length);
    list.forEach((f) => {
      if (!f.type.startsWith("image/")) return toast.error("Please upload an image file");
      if (f.size > 8 * 1024 * 1024) return toast.error("Image must be under 8MB");
      const reader = new FileReader();
      reader.onload = () => setRefs((r) => [...r, reader.result]);
      reader.readAsDataURL(f);
    });
  };

  const generate = async (promptOverride) => {
    const effectivePrompt = typeof promptOverride === "string" ? promptOverride : prompt;
    const enhancing = refs.length > 0;
    if (!enhancing && !effectivePrompt.trim()) return toast.error("Describe the image or upload a photo to enhance");
    setBusy(true);
    try {
      await api.post("/images/generate", {
        prompt: effectivePrompt, style, aspect,
        use_face: useFace,
        reference_images: refs,
      });
      load();
      setPrompt("");
      setRefs([]);
      toast.success(enhancing ? "Photo enhanced" : "Brand image created");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      if (err.response?.status === 402) toast.error(msg, { action: { label: "Upgrade", onClick: () => (window.location.href = "/billing") } });
      else toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => { await api.delete(`/images/${id}`); load(); toast.success("Deleted"); };
  const download = (img) => { const a = document.createElement("a"); a.href = img.image; a.download = `smi-${img.id}.png`; a.click(); };

  return (
    <div className="space-y-6 fade-up">
      <PageGuide title="How the Image Studio works" steps={[
        "AI Generate: describe a visual, pick a style and aspect ratio, then Generate image.",
        "Enhance Photo: upload up to 2 of your own photos to transform them into brand visuals.",
        "Turn on 'Keep my face consistent' to reuse your signature selfie in every image.",
        "Hover a result to download or delete it.",
      ]} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 h-fit" testid="image-form-card">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon size={22} weight="bold" className="text-primary" />
            <h3 className="font-head font-bold text-lg">Brand Image Studio</h3>
          </div>

          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2 mb-5 p-1 bg-secondary">
            <button data-testid="mode-generate" onClick={() => setMode("generate")}
              className={`py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors duration-200 ${mode === "generate" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-white"}`}>
              <Sparkle size={16} weight="fill" /> AI Generate
            </button>
            <button data-testid="mode-enhance" onClick={() => setMode("enhance")}
              className={`py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors duration-200 ${mode === "enhance" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-white"}`}>
              <MagicWand size={16} weight="fill" /> Enhance Photo
            </button>
          </div>

          {/* Upload zone — always visible so you can enhance your own photo any time */}
          <div className="mb-4">
            <label className="overline text-muted-foreground block mb-2">Upload your photo(s) to enhance · optional · up to 2</label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              {refs.map((r, i) => (
                <div key={i} className="relative aspect-square border border-border overflow-hidden">
                  <img src={r} alt="ref" className="w-full h-full object-cover" />
                  <button onClick={() => setRefs(refs.filter((_, j) => j !== i))} data-testid={`remove-ref-${i}`}
                    className="absolute top-1 right-1 bg-black/70 p-1 hover:text-destructive transition-colors duration-200"><X size={12} weight="bold" /></button>
                </div>
              ))}
              {refs.length < 2 && (
                <button onClick={() => { setMode("enhance"); fileRef.current?.click(); }} data-testid="upload-photo-btn"
                  className="aspect-square border border-dashed border-primary/50 flex flex-col items-center justify-center gap-1 text-primary hover:border-primary hover:bg-primary/5 transition-colors duration-200">
                  <UploadSimple size={22} weight="bold" />
                  <span className="text-xs">Upload photo</span>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden data-testid="file-input"
              onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
            {refs.length > 0 && (
              <div>
                <div className="overline text-primary mb-1.5 flex items-center gap-1"><Sparkle size={11} weight="fill" /> ONE-TAP HOLLYWOOD ENHANCE</div>
                <div className="flex flex-wrap gap-1.5">
                  {ENHANCE_PRESETS.map((p) => (
                    <button key={p.label} disabled={busy} onClick={() => generate(p.prompt)} data-testid={`enhance-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-xs border border-primary/40 text-primary px-2.5 py-1 hover:bg-primary hover:text-primary-foreground transition-colors duration-200 disabled:opacity-50">
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Prompt */}
          <div className="mb-4">
            <label className="overline text-muted-foreground block mb-2">
              {mode === "enhance" ? "How should we transform it?" : "Describe the visual"}
            </label>
            <textarea value={prompt} data-testid="image-prompt" onChange={(e) => setPrompt(e.target.value)}
              placeholder={mode === "enhance" ? "Turn this into a polished studio headshot…" : "Confident influencer in a sunlit rooftop cafe…"}
              rows={3} className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200 resize-none" />
          </div>

          {/* Style presets */}
          <div className="mb-4">
            <label className="overline text-muted-foreground block mb-2">Style</label>
            <div className="flex flex-wrap gap-1.5">
              {STYLES.map((s) => (
                <button key={s} onClick={() => setStyle(s)} data-testid={`style-${s.replace(/\s+/g, "-")}`}
                  className={`text-xs px-2.5 py-1 border capitalize transition-colors duration-200 ${style === s ? "border-primary bg-primary/10 text-white" : "border-border text-muted-foreground hover:text-white"}`}>{s}</button>
              ))}
            </div>
          </div>

          {/* Aspect */}
          <div className="mb-5">
            <label className="overline text-muted-foreground block mb-2">Aspect ratio</label>
            <div className="grid grid-cols-3 gap-2">
              {ASPECTS.map((a) => (
                <button key={a.id} onClick={() => setAspect(a.id)} data-testid={`aspect-${a.id}`}
                  className={`py-2 border flex flex-col items-center gap-1.5 transition-colors duration-200 ${aspect === a.id ? "border-primary bg-primary/10 text-white" : "border-border text-muted-foreground hover:text-white"}`}>
                  <span className={`${a.box} border-2 border-current`} />
                  <span className="text-xs">{a.label}</span>
                  <span className="text-[0.6rem] font-mono opacity-70">{a.sub}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Face consistency */}
          <div className="mb-5 border border-border p-3" data-testid="face-consistency">
            {hasFace ? (
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setUseFace(!useFace)} data-testid="use-face-toggle"
                  className={`relative h-5 w-9 rounded-full transition-colors duration-200 shrink-0 ${useFace ? "bg-primary" : "bg-secondary border border-border"}`}>
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all duration-200 ${useFace ? "left-4" : "left-0.5"}`} />
                </button>
                <div>
                  <div className="text-sm font-medium">Keep my face consistent</div>
                  <div className="text-xs text-muted-foreground">Uses your saved signature selfie in every image.</div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                Want every image to look like you? <a href="/profile" className="text-primary underline">Save a signature selfie</a> in your profile.
              </div>
            )}
          </div>

          <button onClick={generate} disabled={busy} data-testid="generate-image-btn"
            className="w-full bg-primary text-primary-foreground font-semibold font-semibold py-3 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
            {busy ? <Spinner label={refs.length ? "Enhancing…" : "Rendering…"} /> : (<><Sparkle size={18} weight="fill" /> {refs.length ? "Enhance my photo" : "Generate image"}</>)}
          </button>
        </Card>

        <div className="lg:col-span-2">
          {images.length === 0 && !busy && <Empty>Your generated brand visuals will appear here.</Empty>}
          <div className="grid grid-cols-2 gap-4">
            {busy && (
              <div className="aspect-square border border-border flex items-center justify-center bg-card">
                <Spinner label={mode === "enhance" ? "Transforming…" : "Rendering…"} />
              </div>
            )}
            {images.map((img) => (
              <div key={img.id} className="group relative border border-border overflow-hidden bg-card" data-testid={`image-${img.id}`}>
                <img src={img.image} alt={img.prompt} className="w-full aspect-square object-cover" />
                {img.from_upload && <span className="absolute top-2 left-2"><Pill tone="high">enhanced</Pill></span>}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-end p-3">
                  <p className="text-xs text-white/80 line-clamp-2 mb-2">{img.prompt}</p>
                  <div className="flex gap-2">
                    <button onClick={() => download(img)} data-testid={`download-${img.id}`} className="p-1.5 bg-white/10 border border-white/20 hover:bg-white/20 transition-colors duration-200"><DownloadSimple size={16} /></button>
                    <button onClick={() => remove(img.id)} data-testid={`delete-img-${img.id}`} className="p-1.5 bg-white/10 border border-white/20 hover:text-destructive transition-colors duration-200"><Trash size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
